import React, { useState } from "react";
import InputField from "@/components/form/InputField";
import TextareaField from "@/components/form/TextAreaField";
import FileUploadField from "@/components/ui/FileUploadField";
import { Separator } from "@/components/ui/Separator";
import WhiteCheck from "@/assets/icons/WhiteCheck.svg";
import RadioButton from "@/components/ui/RadioButton";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  // updateComfortableTopics,
  updateField,
} from "@/redux/feature/creatorApplication/creatorApplicationSlice";
import FileUploadFieldPlus from "@/components/ui/FileUploadFieldPlus";
import MultiSelect from "@/components/ui/MultiSelect";
import { SelectComponetOption } from "@/types/types";
import SelectSkills, { SkillsOptions } from "@/components/ui/SelectSkills";
import MultiLinkField from "./MultiLinkField";
import WardrobeStyleSamples from "@/assets/images/Samples/wardrobe-samples.webp";
import BackgroundSamples from "@/assets/images/Samples/backgroud-samples.webp";
import GlobalSelect from "@/components/ui/GlobalSelectItem";
import { SubmitApplicationInfo } from "@/redux/feature/creatorApplication/creatorApplicationTypes";
import MissingFieldError from "@/components/ui/MissingFieldError";
export const languageOptions = [
  { id: "2", title: "Bengali" },
  { id: "11", title: "English" },
  { id: "6", title: "Gujarati" },
  { id: "1", title: "Hindi" },
  { id: "8", title: "Kannada" },
  { id: "423", title: "Malayalam" },
  { id: "4", title: "Marathi" },
  { id: "9", title: "Odia" },
  { id: "10", title: "Punjabi" },
  { id: "5", title: "Tamil" },
  { id: "3", title: "Telugu" },
  { id: "7", title: "Urdu" },
];
export const comfortableTopics = [
  { id: "finance", title: "Finance" },
  { id: "hr", title: "Human Resource" },
  { id: "marketing", title: "Marketing" },
  { id: "technology", title: "Technology" },
  { id: "education", title: "Education" },
  { id: "others", title: "Others" },
];

export const availableHours = [
  "1-5 hours",
  "6-10 hours",
  "11-20 hours",
  "20+ hours",
];

interface Topic {
  id: string;
  title: string;
}

interface CheckboxProps {
  SelectedOptions: Topic[];
  item: Topic;
}

export type SelectComponentOption = {
  id: string;
  value?: any;
  title: string;
};

export const LanguageSelect = ({ defaultValue }: { defaultValue?: string }) => {
  const dispatch = useAppDispatch();

  // Split the defaultValue string into an array of languages
  const defaultLanguages = defaultValue?.split(", ").filter(Boolean);

  // Filter the default selected items from the available language options
  const defaultSelectedItems = languageOptions.filter((item) =>
    defaultLanguages?.includes(item.title)
  );
  const [selectedLanguages, setSelectedLanguages] =
    useState<SelectComponentOption[]>(defaultSelectedItems);

  const handleSelectLanguage = (options: SelectComponentOption[] | null) => {
    if (options) {
      setSelectedLanguages(options);
      dispatch(
        updateField({
          field: "language",
          value: options.map((option) => option.title).join(", "),
        })
      );
    } else {
      setSelectedLanguages([]);
      dispatch(updateField({ field: "language", value: "" }));
    }
  };

  return (
    <div>
      <MultiSelect
        label="What are the languages that you can speak?"
        options={languageOptions}
        selectedOptions={selectedLanguages}
        onSelect={handleSelectLanguage}
        placeholder="Select languages..."
        showOtherInputField={true}
      />
    </div>
  );
};

const ProfessionalDetails = ({
  missingFields,
}: {
  missingFields: string[];
}) => {
  const dispatch = useAppDispatch();
  const submitApplicationInfo = useAppSelector(
    (state) => state.signUp.SubmitApplicationInfo
  );

  // console.log(submitApplicationInfo.brandsDontWantToWorkWith);
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      updateField({
        field: "experienceDescription",
        value: e.target.value,
      })
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateField({
        field: e.target.name as keyof typeof submitApplicationInfo,
        value: e.target.value,
      })
    );
  };

  const [profileLinkError, setProfileLinkError] = useState<string>("");

  const handleProfileLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    dispatch(
      updateField({
        field: e.target.name as keyof typeof submitApplicationInfo,
        value: e.target.value,
      })
    );

    const isLinkedIn = value.includes("linkedin.com");
    const isInstagram = value.includes("instagram.com");

    if (!isLinkedIn && !isInstagram) {
      setProfileLinkError("Only LinkedIn or Instagram links are allowed");
    } else {
      setProfileLinkError("");
      dispatch(
        updateField({
          field: "profileLink",
          value,
        })
      );
    }
  };

  const handleAvailabilityChange = (hours: string) => {
    dispatch(
      updateField({
        field: "availableHoursPerWeek",
        value: hours,
      })
    );
  };

  const handleWeekendAvailability = (isAvailable: boolean) => {
    dispatch(
      updateField({
        field: "weekendAvailability",
        value: isAvailable,
      })
    );
  };
  return (
    <div className="bg-white border border-[#D0D5DD] rounded-xl p-6">
      <h4 className="text-base leading-6 tracking-[-1%] font-semibold mb-4">
        Professional Details
      </h4>
      <form className="space-y-6">
        <div id="profileLink">
          <InputField
            name="profileLink"
            label="LinkedIn/Instagram Profile Link"
            showLink={true}
            value={submitApplicationInfo.profileLink}
            onChange={handleProfileLinkChange}
            error={
              profileLinkError ||
              (missingFields.includes("profileLink")
                ? "Profile link is Required"
                : "")
            }
          />
        </div>
        <div id="experienceDescription">
          <TextareaField
            onChange={handleTextAreaChange}
            name="experienceDescription"
            label="Please describe your experience as an anchor/speaker, also specify the brands you have worked with:"
            placeholder="Please Describe.."
            height="100px"
            value={submitApplicationInfo.experienceDescription}
            error={
              missingFields.includes("experienceDescription")
                ? "Experience is Required"
                : ""
            }
          />
        </div>
        <Separator />

        <div id="availableHoursPerWeek">
          <p className="text-[14px] mb-3 text-[#344054] font-medium">
            How many hours per week are you available for freelance
            anchor/speaker opportunities?
          </p>
          <div className="flex gap-x-8 gap-y-4 flex-wrap items-center">
            {availableHours.map((item, i) => (
              <div key={i} className="gap-x-2 flex items-center">
                <RadioButton
                  onChange={() => handleAvailabilityChange(item)}
                  checked={submitApplicationInfo.availableHoursPerWeek === item}
                />
                <span className="text-sm font-medium text-gray-500">
                  {item}
                </span>
              </div>
            ))}
          </div>
          {missingFields.includes("availableHoursPerWeek") && (
            <p className="mt-2 text-red-500 text-sm">
              How many per week available is required
            </p>
          )}
        </div>

        <div id="videoLinks">
          <p className="text-[14px] mb-3 text-[#344054] font-medium">
            Add links to your previous videos with you speaking in it (UGC
            preferred)
          </p>
          <div>
            <MultiLinkField
              links={submitApplicationInfo.videoLinks}
              onLinksChange={(updatedLinks) => {
                dispatch(
                  updateField({
                    field: "videoLinks",
                    value: updatedLinks,
                  })
                );
              }}
            />
          </div>

          {missingFields.includes("videoLinks") && (
            <p className="mt-2 text-red-500 text-sm">This field is required</p>
          )}
        </div>

        <div id="weekendAvailability">
          <p className="text-[14px] mb-3 text-[#344054] font-medium">
            Are you available to work weekends or evenings if needed?
          </p>
          <div className="flex flex-col gap-y-4">
            <div className="gap-x-2 flex items-center">
              <RadioButton
                onChange={() => handleWeekendAvailability(true)}
                checked={submitApplicationInfo.weekendAvailability === true}
              />
              <span className="text-sm font-medium text-gray-500">Yes</span>
            </div>
            <div className="gap-x-2 flex items-center">
              <RadioButton
                onChange={() => handleWeekendAvailability(false)}
                checked={submitApplicationInfo.weekendAvailability === false}
              />
              <span className="text-sm font-medium text-gray-500">No</span>
            </div>
          </div>
          {missingFields.includes("weekendAvailability") && (
            <p className="mt-2 text-red-500 text-sm">
              Available for weekend or evenings is required
            </p>
          )}
        </div>

        <div id="language">
          <LanguageSelect />
          <MissingFieldError field="language" missingFields={missingFields} />
        </div>

        <div id="brandsDontWantToWorkWith">
          <InputField
            name="brandsDontWantToWorkWith"
            label="Industries/Topics you are not comfortable to work with?"
            placeholder="Enter here"
            value={submitApplicationInfo.brandsDontWantToWorkWith}
            onChange={handleInputChange}
            error={
              missingFields.includes("brandsDontWantToWorkWith")
                ? "Uncomfortable industry/topic is Required"
                : ""
            }
          />
        </div>
        <div id="skills">
          <SelectSkills />
          {missingFields.includes("skills") && (
            <p className="mt-2 text-red-500 text-sm">
              Please Select at least one skill
            </p>
          )}
        </div>
        <div id="topics">
          <SelectTopics />
          {missingFields.includes("topics") && (
            <p className="mt-2 text-red-500 text-sm">
              Please Select at least one field
            </p>
          )}
        </div>
        <div id="lastSalary">
          <InputField
            name="lastSalary"
            label="Last drawn salary?"
            placeholder="Enter here"
            value={submitApplicationInfo.lastSalary}
            onChange={handleInputChange}
            error={
              missingFields.includes("lastSalary")
                ? "Last drawn salary is Required"
                : ""
            }
            type="number"
          />
        </div>
        <div id="expectedPay">
          <InputField
            name="expectedPay"
            label="What is your expected pay per month or video?"
            placeholder="Enter here"
            value={submitApplicationInfo.expectedPay}
            onChange={handleInputChange}
            error={
              missingFields.includes("expectedPay")
                ? "Expected pay is Required"
                : ""
            }
            type="number"
          />
        </div>

        <div id="wardrobeStyle">
          <label className="text-[#344054] text-[13px] leading-[18px] font-medium">
            Upload 3-4 images of wardrobe options you have
          </label>
          <FileUploadFieldPlus
            accept={{
              "image/png": [],
              "image/jpeg": [],
            }}
            maxFiles={4}
            onFilesUploaded={(value) => {
              console.log(value);
              return dispatch(
                updateField({
                  field: "wardrobeStyle",
                  value: value,
                })
              );
            }}
          />
          <div className="mt-[18px]">
            <img src={WardrobeStyleSamples} alt="" />
          </div>
          {missingFields.includes("wardrobeStyle") && (
            <p className="mt-2 text-red-500 text-sm">Wardrobe is required</p>
          )}
        </div>

        <div id="backgroundStyle">
          <label className="text-[#344054] text-[13px] leading-[18px] font-medium">
            Upload 3-4 images of background options you have
          </label>
          <FileUploadFieldPlus
            accept={{
              "image/png": [],
              "image/jpeg": [],
            }}
            onFilesUploaded={(value) =>
              dispatch(
                updateField({
                  field: "backgroundStyle",
                  value: value,
                })
              )
            }
          />
          <div className="mt-[18px]">
            <img src={BackgroundSamples} alt="" />
          </div>
          {missingFields.includes("backgroundStyle") && (
            <p className="mt-2 text-red-500 text-sm">
              Background image is required
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

// export const Checkbox: React.FC<CheckboxProps> = ({
//   SelectedOptions,
//   item,
// }) => {
//   const dispatch = useAppDispatch();
//   const isSelectedOption = SelectedOptions.find((data) => data.id === item.id)
//     ? true
//     : false;

//   const handleSelect = (item: Topic) => {
//     dispatch(updateComfortableTopics(item.id));
//   };

//   return (
//     <div className="inline-flex items-center">
//       <label
//         className="relative flex items-center rounded-full cursor-pointer"
//         htmlFor={`checkbox-${item.id}`}
//       >
//         <input
//           checked={isSelectedOption}
//           onChange={() => handleSelect(item)}
//           type="checkbox"
//           className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-[1.4px] border-[#D0D5DD] transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-[#17457D] checked:bg-[#17457D] checked:before:bg-[#17457D] hover:before:opacity-10"
//           id={`checkbox-${item.id}`}
//         />
//         <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
//           <WhiteCheck />
//         </span>
//       </label>
//     </div>
//   );
// };

export default ProfessionalDetails;
type SelectOption = {
  id: string;
  title: string;
};
const TopicOptions: SelectOption[] = [
  { id: "Finance", title: "Finance" },
  { id: "Human Resources", title: "Human Resources" },
  { id: "Marketing", title: "Marketing" },
  { id: "Technology", title: "Technology" },
  { id: "Education", title: "Education" },
  { id: "Beauty", title: "Beauty" },
  { id: "Food", title: "Food" },
  { id: "Health & Wellness", title: "Health & Wellness" },
  { id: "Travel & Lifestyle", title: "Travel & Lifestyle" },
];

export const SelectTopics = ({
  defaultValue,
  setValue,
}: {
  setValue?: (value: any) => void;
  defaultValue?: string;
}) => {
  const dispatch = useAppDispatch();

  // Split the defaultValue string into an array of options
  const defaultSelectedProps = defaultValue?.split(", ")?.filter(Boolean);
  // Filter the default selected items from the available options
  const defaultSelectedItems = TopicOptions.filter((item) =>
    defaultSelectedProps?.includes(item.id)
  );
  const [selectedProps, setSelectedProps] =
    useState<SelectComponetOption[]>(defaultSelectedItems);

  const handleSelectTopics = (options: SelectComponetOption[] | null) => {
    if (options) {
      setSelectedProps(options);
      // Bad Practise
      dispatch(
        updateField({
          field: "topics",
          value: options.map((option) => option.title).join(", "),
        })
      );
      const value = options.map((option) => option.title).join(", ");
      setValue?.(value);
    } else {
      setSelectedProps([]);
      dispatch(updateField({ field: "topics", value: [] }));
      setValue?.("");
    }
  };

  return (
    <div>
      <MultiSelect
        label=" What topics are you comfortable speaking about?"
        options={TopicOptions}
        selectedOptions={selectedProps}
        onSelect={handleSelectTopics}
        placeholder="Select Options..."
        showOtherInputField={true}
      />
    </div>
  );
};
